import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Bell, X, CheckCircle2 } from 'lucide-react';
import { PUSH_SUPPORTED, getPushState, enablePush } from '../../lib/pushNotifications';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const DISMISS_KEY = 'jixxeo_push_banner_dismissed_until';

/**
 * One-tap push permission banner.
 * - Only shown to logged-in users.
 * - Hidden if permission is already granted/denied or user dismissed in the last 7 days.
 * - Appears bottom-center on mobile, bottom-right on desktop.
 */
export default function PushPermissionBanner() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !PUSH_SUPPORTED) return;
    (async () => {
      const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (Date.now() < dismissedUntil) return;
      const state = await getPushState();
      // Only prompt when permission is still "default" (never asked) and server supports push
      if (state.permission === 'default' && state.available && !state.subscribed) {
        // Delay 20s so we don't nag immediately on app launch
        const timeout = setTimeout(() => setVisible(true), 20_000);
        return () => clearTimeout(timeout);
      }
    })();
  }, [user]);

  if (!visible) return null;

  const dismiss = (days = 7) => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
    setVisible(false);
  };

  const enable = async () => {
    setBusy(true);
    const r = await enablePush();
    setBusy(false);
    if (r.ok) {
      toast.success('Notifications enabled — Jixxeo AI will ping you when your video is ready.');
      setVisible(false);
    } else if (r.reason === 'denied') {
      toast.info('No worries — in-app notifications still work.');
      dismiss(30);
    } else {
      toast.error('Could not enable notifications. Try again later.');
      dismiss(1);
    }
  };

  return (
    <div
      data-testid="push-permission-banner"
      className="fixed left-3 right-3 sm:left-auto sm:right-6 sm:max-w-sm z-50 bottom-20 sm:bottom-6
                 glass rounded-2xl border border-violet-500/40 p-4 shadow-2xl shadow-violet-500/20"
      role="dialog"
      aria-labelledby="push-banner-title"
    >
      <button data-testid="push-banner-close" onClick={() => dismiss()} className="absolute top-2 right-2 w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl gradient-brand flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <div id="push-banner-title" className="font-display font-black text-base">Stay updated</div>
          <div className="text-xs text-slate-400 mt-0.5">
            Allow Jixxeo AI to notify you when your videos finish, when you get a new follower, and about important updates.
          </div>
          <div className="flex gap-2 mt-3">
            <Button data-testid="push-banner-enable" onClick={enable} disabled={busy} size="sm" className="gradient-brand text-white font-bold">
              {busy ? 'Enabling…' : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Enable Notifications</>}
            </Button>
            <Button data-testid="push-banner-later" onClick={() => dismiss()} size="sm" variant="outline">
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
